import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Users, CreditCard, DollarSign, TrendingUp, AlertTriangle, UserPlus,
  ArrowDownCircle, Webhook, Mail, CheckCircle2, FileDown, Heart, Repeat,
} from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/common/Loading";
import { StatCard } from "@/components/dashboard/StatCard";
import AnalyticsDatasets from "@/components/admin/AnalyticsDatasets";
import { generateReport } from "@/lib/analyticsReport";

const TIER_LABELS = { standard: "VIP", pro: "PRO", premium: "CAPITAL", business: "TEAMS" };
const PALETTE = ["#0d9488", "#0891b2", "#f59e0b", "#6366f1", "#ec4899", "#64748b"];
const PRESETS = [{ label: "7d", days: 7 }, { label: "30d", days: 30 }, { label: "90d", days: 90 }, { label: "1y", days: 365 }];

const isoDate = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => isoDate(new Date(Date.now() - n * 86400000));
const fmtUnix = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const relTime = (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

function ChartCard({ title, sub, children, testId }) {
  return (
    <Card className="p-5" data-testid={testId}>
      <h3 className="text-sm font-[680] text-foreground">{title}</h3>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      <div className="mt-3 h-[240px] w-full">{children}</div>
    </Card>
  );
}

function DonutCard({ title, data, testId }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <ChartCard title={title} testId={testId}>
      {total === 0 ? (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No data</div>
      ) : (
        <div className="flex h-full items-center gap-4">
          <ResponsiveContainer width="55%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex-1 space-y-1.5">
            {data.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-foreground/80">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
                  {d.name}
                </span>
                <span className="font-[600] text-foreground">{d.value} · {total ? Math.round((d.value / total) * 100) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ChartCard>
  );
}

export default function AdminAnalytics() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(isoDate(new Date()));
  const [granularity, setGranularity] = useState("day");
  const [reporting, setReporting] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["admin-analytics", from, to, granularity],
    queryFn: async () => (await api.get(`/admin/analytics?from=${from}&to=${to}&granularity=${granularity}`)).data,
  });

  const onReport = async () => {
    setReporting(true);
    try {
      await generateReport(api, data, from, to);
    } catch (e) {
      toast.error(e?.message === "popup-blocked" ? "Allow pop-ups for this site to open the report." : "Couldn't generate the report.");
    } finally {
      setReporting(false);
    }
  };

  const tierDonut = useMemo(
    () => Object.entries(data?.members?.by_tier || {}).map(([k, v]) => ({ name: TIER_LABELS[k] || k, value: v })),
    [data]
  );
  const intervalDonut = useMemo(() => {
    const bi = data?.billing?.by_interval || {};
    return [
      { name: "Monthly", value: bi.month || 0 },
      { name: "Annual", value: bi.year || 0 },
    ].filter((d) => d.value > 0);
  }, [data]);

  const setPreset = (days) => { setFrom(daysAgo(days - 1)); setTo(isoDate(new Date())); };

  if (isLoading || !data) return <Loading />;
  const b = data.billing;
  const m = data.members;
  const d = data.donations;
  const emailLive = (b.last_webhook || data.email.by_mode.mailersend);

  return (
    <div data-testid="admin-analytics">
      {/* Filter bar */}
      <Card className="flex flex-wrap items-end justify-between gap-4 p-4" data-testid="analytics-filter-bar">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex gap-1">
            {PRESETS.map((p) => (
              <Button key={p.label} size="sm" variant="secondary" onClick={() => setPreset(p.days)} data-testid={`analytics-preset-${p.label}`}>{p.label}</Button>
            ))}
          </div>
          <div>
            <label className="block text-[11px] text-muted-foreground">From</label>
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" data-testid="analytics-from" />
          </div>
          <div>
            <label className="block text-[11px] text-muted-foreground">To</label>
            <Input type="date" value={to} min={from} max={isoDate(new Date())} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" data-testid="analytics-to" />
          </div>
          <div>
            <label className="block text-[11px] text-muted-foreground">Granularity</label>
            <div className="flex gap-1">
              {["day", "week", "month"].map((g) => (
                <Button key={g} size="sm" variant={granularity === g ? "default" : "secondary"} onClick={() => setGranularity(g)} data-testid={`analytics-gran-${g}`}>{g}</Button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={onReport} disabled={reporting} data-testid="analytics-generate-report">
            <FileDown className="mr-1.5 h-4 w-4" /> {reporting ? "Generating…" : "Generate report"}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching} data-testid="analytics-refresh">
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </Card>

      {/* Billing KPIs */}
      <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={CreditCard} label="Active subscribers" value={b.active_subscribers} testId="analytics-kpi-subscribers" />
        <StatCard icon={DollarSign} label="MRR (gross est.)" value={fmtMoney(b.mrr)} sub={`ARR ${fmtMoney(b.arr)}`} testId="analytics-kpi-mrr" />
        <StatCard icon={UserPlus} label="New members" value={m.new_in_range} sub="in range" testId="analytics-kpi-new-members" />
        <StatCard icon={TrendingUp} label="New subscriptions" value={b.new_subscribers} sub="in range" testId="analytics-kpi-new-subs" />
        <StatCard icon={DollarSign} label="Payments collected" value={fmtMoney(b.payments.paid_amount)} sub={`${b.payments.paid_count} invoices`} testId="analytics-kpi-collected" />
        <StatCard icon={AlertTriangle} label="Failed payments" value={b.payments.failed_count} testId="analytics-kpi-failed" />
        <StatCard icon={ArrowDownCircle} label="Cancellations scheduled" value={b.cancel_scheduled} testId="analytics-kpi-cancel" />
        <StatCard icon={Users} label="Total members" value={m.total} testId="analytics-kpi-total" />
      </div>

      {/* Charts */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ChartCard title="New members" sub={`${data.range.from} → ${data.range.to} · by ${data.range.granularity}`} testId="analytics-chart-signups">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={m.signups_series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gSignup" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0d9488" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} width={32} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#0d9488" strokeWidth={2} fill="url(#gSignup)" name="New members" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue collected" sub="Paid invoices by month (recent)" testId="analytics-chart-revenue">
          {data.revenue_series.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No paid invoices in range</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.revenue_series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={42} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="amount" fill="#0d9488" radius={[4, 4, 0, 0]} name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Tier changes" sub="Upgrades vs downgrades over time" testId="analytics-chart-tierchanges">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.tier_changes_series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} width={32} />
              <Tooltip />
              <Bar dataKey="upgrades" fill="#0d9488" radius={[3, 3, 0, 0]} name="Upgrades" />
              <Bar dataKey="downgrades" fill="#f59e0b" radius={[3, 3, 0, 0]} name="Downgrades" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <DonutCard title="Members by tier" data={tierDonut} testId="analytics-chart-tierdonut" />
      </div>

      {/* Donations */}
      <div className="mt-6" data-testid="analytics-donations">
        <h2 className="text-lg font-[680] text-foreground">Donations</h2>
        <p className="text-sm text-muted-foreground">
          Voluntary support from the pricing page. Tracked separately from plan revenue — donations never affect subscription MRR/ARR.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Heart} label="Donated in range" value={fmtMoney(d.total_in_range)} sub={`${d.count_in_range} contribution${d.count_in_range === 1 ? "" : "s"}`} testId="analytics-kpi-donated" />
          <StatCard icon={DollarSign} label="One-time" value={fmtMoney(d.one_time.total)} sub={`${d.one_time.count} payments`} testId="analytics-kpi-donation-onetime" />
          <StatCard icon={Repeat} label="Monthly donors" value={d.active_monthly_donors} sub={`${fmtMoney(d.monthly_recurring)} / month`} testId="analytics-kpi-donation-monthly" />
          <StatCard icon={TrendingUp} label="All-time donated" value={fmtMoney(d.all_time_total)} sub={`${d.all_time_count} total`} testId="analytics-kpi-donation-alltime" />
        </div>
        <div className="mt-5">
          <ChartCard title="Donations collected" sub={`${data.range.from} → ${data.range.to} · by ${data.range.granularity}`} testId="analytics-chart-donations">
            {d.total_in_range === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No donations in range</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.donations_series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={42} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="amount" fill="#0f766e" radius={[4, 4, 0, 0]} name="Donations" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </div>

      {/* Billing + Email health */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="p-5" data-testid="analytics-webhook-card">
          <div className="flex items-center gap-2"><Webhook className="h-4 w-4 text-primary" /><h3 className="text-sm font-[680] text-foreground">Webhook health</h3></div>
          {b.last_webhook ? (
            <div className="mt-3 text-sm">
              <div className="flex items-center gap-2 text-teal-700"><CheckCircle2 className="h-4 w-4" /> Receiving events</div>
              <p className="mt-2 text-muted-foreground">Last event <span className="font-[600] text-foreground">{b.last_webhook.type}</span></p>
              <p className="text-muted-foreground">{relTime(b.last_webhook.received_at)} · {new Date(b.last_webhook.received_at).toLocaleString()}</p>
            </div>
          ) : (
            <p className="mt-3 text-sm text-amber-700" data-testid="analytics-webhook-none">
              No webhook events received yet. Register the endpoint in Stripe → Developers → Webhooks pointing to <code className="text-xs">/api/webhook/stripe-subscription</code>.
            </p>
          )}
          {intervalDonut.length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-xs font-[600] text-muted-foreground">Active subscribers by billing</p>
              <div className="mt-2 flex gap-4 text-sm">
                <span>Monthly <b>{(b.by_interval || {}).month || 0}</b></span>
                <span>Annual <b>{(b.by_interval || {}).year || 0}</b></span>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="analytics-failed-card">
          <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /><h3 className="text-sm font-[680] text-foreground">Recent failed payments</h3></div>
          {(b.payments.recent_failed || []).length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No failed payments in this range.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {b.payments.recent_failed.map((f) => (
                <div key={f.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 text-sm" data-testid={`analytics-failed-${f.id}`}>
                  <div className="min-w-0">
                    <div className="truncate font-[560] text-foreground">{f.email || "—"}</div>
                    <div className="text-xs text-muted-foreground">{fmtUnix(f.created)} · {f.status}</div>
                  </div>
                  <span className="font-[640] text-amber-700">{f.currency} {Number(f.amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="analytics-email-card">
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /><h3 className="text-sm font-[680] text-foreground">Email health</h3></div>
          <p className="mt-3 text-2xl font-[720] text-foreground">{data.email.sent_total_in_range}<span className="ml-1.5 text-xs font-[400] text-muted-foreground">sent in range</span></p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(data.email.by_mode).map(([k, v]) => (
              <span key={k} className={`rounded-full px-2 py-0.5 text-[11px] font-[560] ${k === "mailersend" ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"}`}>
                {k === "mailersend" ? "Delivered" : "Log only"}: {v}
              </span>
            ))}
          </div>
          <div className="mt-3 space-y-1 border-t border-border pt-3 text-xs">
            {Object.entries(data.email.by_tag).slice(0, 6).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-[600] text-foreground">{v}</span></div>
            ))}
          </div>
        </Card>
      </div>

      {/* Filterable datasets */}
      <div className="mt-8" data-testid="analytics-datasets">
        <h2 className="text-lg font-[680] text-foreground">Datasets</h2>
        <p className="text-sm text-muted-foreground">Browse and filter every record. The date range above applies; each table adds its own filters.</p>
        <div className="mt-4">
          <AnalyticsDatasets from={from} to={to} />
        </div>
      </div>
    </div>
  );
}
